<template>
  <b-container fluid>
    <b-row class="page-heading">
      <b-col>
        <h1>Produits à la Une</h1>
        <p class="text-muted">
          Sélectionnez jusqu'à {{ maxItems }} produits mis en avant sur la page d'accueil de la
          boutique. L'ordre de la liste est celui du carrousel : glissez les lignes pour le changer.
        </p>
      </b-col>
    </b-row>

    <div v-if="isBusy" class="loader"></div>
    <p v-if="isBusy" class="text-center">Les données sont en cours de chargement...</p>

    <b-row v-else>
      <b-col lg="9">
        <div class="ibox">
          <div class="ibox-title">
            <i class="far fa-star"></i> Sélection
            <span class="float-right text-muted" style="font-weight: 400; font-size: 13px">
              {{ selection.length }} / {{ maxItems }}
            </span>
          </div>
          <div class="ibox-content">
            <b-form-group label="Ajouter un produit" class="fp-add">
              <ProductAutocomplete
                :key="autocompleteKey"
                :value="null"
                :remote="true"
                statusSlug="product_on_sale"
                :disabled="isFull"
                placeholder="Rechercher un produit par nom ou code interne…"
                @input="add"
              />
              <small v-if="isFull" class="text-muted">
                Maximum atteint : retirez un produit pour en ajouter un autre.
              </small>
              <small v-else class="text-muted">
                Seuls les produits disponibles à la boutique sont proposés.
              </small>
            </b-form-group>

            <p v-if="!selection.length" class="fp-empty">
              <i class="far fa-store-slash"></i>
              Aucun produit sélectionné : le bloc « Produits à la une » n'apparaît pas sur la page
              d'accueil.
            </p>

            <draggable v-else v-model="selection" handle=".fp-line__handle" class="fp-list">
              <div v-for="(line, index) in selection" :key="line.id" class="fp-line">
                <div class="fp-line__handle" title="Glisser pour réordonner">
                  <i class="far fa-grip-lines"></i>
                </div>
                <span class="fp-line__rank">{{ index + 1 }}</span>
                <div class="fp-line__body">
                  <span v-if="line.code" class="fp-line__code">{{ line.code }}</span>
                  <span class="fp-line__name">{{ line.name }}</span>
                  <span v-if="line.warning" class="fp-line__warning">
                    <i class="far fa-exclamation-triangle"></i> {{ line.warning }}
                  </span>
                </div>
                <button
                  type="button"
                  class="fp-line__remove"
                  title="Retirer de la sélection"
                  @click="remove(index)"
                >
                  <i class="far fa-trash-alt"></i>
                </button>
              </div>
            </draggable>
          </div>
        </div>
      </b-col>

      <b-col lg="3">
        <div class="ibox">
          <div class="ibox-title"><i class="far fa-clipboard-check"></i> Récapitulatif</div>
          <div class="ibox-content">
            <dl class="mb-3">
              <dt class="text-muted" style="font-weight: 400">Produits affichés</dt>
              <dd>{{ displayableCount }} / {{ maxItems }}</dd>
              <dt v-if="unavailableCount" class="text-muted" style="font-weight: 400">
                Lignes sans effet
              </dt>
              <dd v-if="unavailableCount" class="text-danger">{{ unavailableCount }}</dd>
            </dl>
            <p v-if="unavailableCount" class="fp-recap__hint">
              Un produit arrêté ou supprimé du catalogue ne s'affiche plus en boutique. Retirez ces
              lignes, ou remplacez-les.
            </p>
            <b-button variant="primary" block :disabled="saving" @click="submit">
              <i class="far fa-check"></i> {{ saving ? "Enregistrement…" : "Enregistrer" }}
            </b-button>
          </div>
        </div>
      </b-col>
    </b-row>
  </b-container>
</template>

<script>
import draggable from "vuedraggable";
import ProductAutocomplete from "@/components/Global/ProductAutocomplete";

const SETTING = "home_new_products";
const MAX_ITEMS = 10;

/**
 * AC-002 — Écran des Produits à la Une (SFD ESM 2.8 § Animation Commerciale).
 *
 * La sélection est stockée dans le réglage boutique `home_new_products` : un tableau JSON
 * d'identifiants produits, dans l'ordre d'affichage. Cet écran ne manipule que ce réglage,
 * mais garde son propre chemin parce que l'ordre et le plafond n'entrent pas dans le
 * formulaire générique des paramètres.
 *
 * Le catalogue dépasse 4 000 produits : rien n'est préchargé. À l'ouverture, seuls les
 * produits de la sélection sont chargés — sans filtre de statut, pour pouvoir signaler
 * ceux qui ne s'affichent plus.
 */
export default {
  name: "FeaturedProductForm",
  components: { draggable, ProductAutocomplete },
  data() {
    return {
      maxItems: MAX_ITEMS,
      settingId: null,
      // Identifiants dans l'ordre voulu ; les libellés viennent du store.
      selectedIds: [],
      isBusy: true,
      saving: false,
      // Remonté à chaque ajout pour vider le champ de recherche (v-select non contrôlé ici).
      autocompleteKey: 0,
    };
  },
  computed: {
    selection: {
      get() {
        return this.selectedIds.map((id) => {
          const product = this.$store.getters["product/getItem"](id);
          if (!product) {
            return { id, code: null, name: "Produit #" + id, warning: "supprimé du catalogue" };
          }
          return {
            id: id,
            code: product.internal_code,
            name: product.name,
            warning:
              product.status_slug === "product_on_sale" ? null : "n'est plus disponible à la boutique",
          };
        });
      },
      // vuedraggable réécrit la liste entière après un déplacement.
      set(lines) {
        this.selectedIds = lines.map((line) => line.id);
      },
    },
    isFull() {
      return this.selectedIds.length >= MAX_ITEMS;
    },
    unavailableCount() {
      return this.selection.filter((line) => line.warning).length;
    },
    displayableCount() {
      return this.selectedIds.length - this.unavailableCount;
    },
  },
  mounted() {
    this.$store
      .dispatch("axiloc_setting/getAll", { wait: true })
      .then(() => {
        const setting = this.$store.getters["axiloc_setting/getSetting"](SETTING);
        this.settingId = setting ? setting.id : null;
        this.selectedIds = this.parse(setting ? setting.value : null);

        return this.$store.dispatch("product/getAllForSelectByIds", {
          wait: true,
          ids: this.selectedIds,
        });
      })
      .then(() => {
        this.isBusy = false;
      })
      .catch(() => {
        this.isBusy = false;
      });
  },
  methods: {
    /**
     * Lit la valeur du réglage : une chaîne JSON venue de l'API — ou déjà un tableau, quand
     * le store porte encore ce qui vient d'être envoyé plutôt que la réponse du serveur.
     * Tolère une valeur nulle (sélection jamais renseignée) et un contenu illisible.
     */
    parse(value) {
      if (!value) {
        return [];
      }
      if (Array.isArray(value)) {
        return value.map((id) => parseInt(id, 10)).filter((id) => id);
      }
      try {
        const parsed = JSON.parse(value);
        return Array.isArray(parsed) ? parsed.map((id) => parseInt(id, 10)).filter((id) => id) : [];
      } catch (e) {
        return [];
      }
    },
    add(productId) {
      if (!productId) {
        return;
      }
      if (this.selectedIds.indexOf(productId) !== -1) {
        this.$toastr.warning("Ce produit est déjà dans la sélection");
        return;
      }
      if (this.isFull) {
        this.$toastr.warning("La sélection est limitée à " + MAX_ITEMS + " produits");
        return;
      }
      this.selectedIds.push(productId);
      this.autocompleteKey += 1;
    },
    remove(index) {
      this.selectedIds.splice(index, 1);
    },
    submit() {
      if (!this.settingId) {
        this.$toastr.error("Le paramètre boutique des Produits à la Une est introuvable");
        return;
      }
      this.saving = true;
      this.$store
        .dispatch("axiloc_setting/saveItem", {
          id: this.settingId,
          setting: SETTING,
          // Une sélection vidée doit partir en tableau vide, jamais en null : c'est ainsi
          // qu'on retire le bloc de la page d'accueil.
          value: this.selectedIds,
        })
        // Le serveur écarte à l'enregistrement ce qui n'est plus affichable : on relit ce
        // qui a réellement été retenu plutôt que d'afficher une sélection optimiste. Le
        // rechargement est explicite — celui que déclenche saveItem n'est pas attendu.
        .then(() => this.$store.dispatch("axiloc_setting/getItem", { id: this.settingId, wait: true }))
        .then(() => {
          this.saving = false;
          const setting = this.$store.getters["axiloc_setting/getSetting"](SETTING);
          this.selectedIds = this.parse(setting ? setting.value : null);
        })
        .catch(() => {
          this.saving = false;
        });
    },
  },
};
</script>

<style scoped>
.fp-add {
  max-width: 640px;
}
.fp-empty {
  margin: 0;
  padding: 24px;
  text-align: center;
  color: #909499;
  background: #fafbfc;
  border: 1px dashed #e7eaec;
  border-radius: 4px;
}
.fp-empty i {
  margin-right: 6px;
}
.fp-line {
  display: flex;
  align-items: center;
  padding: 10px 14px;
  margin-bottom: 8px;
  background: #fff;
  border: 1px solid #e7eaec;
  border-left: 3px solid #1ab394;
  border-radius: 4px;
}
.fp-line__handle {
  flex: none;
  margin-right: 10px;
  color: #c8ccd0;
  cursor: grab;
}
.fp-line__handle:active {
  cursor: grabbing;
}
.fp-line__rank {
  flex: none;
  width: 24px;
  margin-right: 10px;
  font-size: 12px;
  font-weight: 700;
  color: #909499;
  text-align: center;
}
.fp-line__body {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  min-width: 0;
  flex: 1;
}
.fp-line__code {
  flex: none;
  margin-right: 8px;
  padding: 1px 6px;
  font-size: 11px;
  font-weight: 600;
  font-family: "SFMono-Regular", Menlo, Consolas, monospace;
  color: #1ab394;
  background: #eaf7f3;
  border-radius: 3px;
  white-space: nowrap;
}
.fp-line__name {
  color: #34495e;
  font-size: 13px;
  overflow: hidden;
  text-overflow: ellipsis;
}
.fp-line__warning {
  margin-left: 10px;
  font-size: 12px;
  font-weight: 600;
  color: #f8ac59;
}
.fp-line__warning i {
  margin-right: 4px;
}
.fp-line__remove {
  flex: none;
  background: none;
  border: 0;
  color: #909499;
  cursor: pointer;
  font-size: 14px;
  line-height: 1;
  padding: 2px 6px;
}
.fp-line__remove:hover {
  color: #d3455a;
}
.fp-recap__hint {
  font-size: 12px;
  color: #909499;
}
</style>



// WEBPACK FOOTER //
// src/components/FeaturedProduct/FeaturedProductForm.vue